import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { store, useAppSelector } from '@/app/store';

import { RoleContext } from '@/app/api/common';
import { checkNoError } from '@/features/channels/channelsSlice';
import {
  EPG_MAX_SOURCES,
  EpgStatus,
  copyEpgStatus,
  epgStatusUrl,
} from '@/features/master/masterSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import { ButtonsModal } from '@/components/Common/Modals';

import {
  AddRow,
  InfoBanner,
  Pill,
  PillTone,
  SettingsCard,
  SettingsInput,
  ToggleRow,
  su,
} from '@/pages/Settings/settingsUi';

import {
  RowStatusKind,
  SourceEdit,
  addSource,
  buildEpgSourcesPayload,
  canAddSource,
  formatWhen,
  listChanged,
  moveSource,
  removeSource,
  rowError,
  rowPending,
  rowStatusKind,
  sourcesInvalid,
  startStatusPoll,
  statusForRow,
  toSourceEdits,
} from '@/pages/Settings/epgSources';

// The status endpoint's POST refetches asynchronously, so the snapshot it
// returns still describes the pre-refresh state. Look again shortly after.
const REFRESH_RECHECK_MS = 4000;

const STATUS_TONE: { [kind in RowStatusKind]: PillTone } = {
  unsaved:		'warn',
  unavailable:	'info',
  unknown:		'info',
  disabled:		'bad',
  failed:		'bad',
  stale:		'warn',
  ok:			'ok',
};

// ---------------------------------------------------------------------------

interface SourceRowProps {
  source:			SourceEdit;
  index:			number;
  all:				SourceEdit[];
  triedSave:		boolean;
  isLoaded:			boolean;
  status:			EpgStatus | null;
  statusFailed:		boolean;
  update:			(patch: Partial<SourceEdit>) => any;
  move:				(delta: number) => any;
  remove:			() => any;
}

const SourceRow = (props: SourceRowProps) => {
  const { t } = useTranslation();

  const src = props.source;
  const err = rowError(src, props.index, props.all);
  const pending = rowPending(src, props.index);

  const st = statusForRow(props.status, src);
  const kind = rowStatusKind(src, st, !!props.status, props.statusFailed);
  const dropped = st ? (st.dropped_duplicates || 0) : 0;

  const arrow = (icon: string, delta: number, caption: string) => {
    const enabled = delta < 0
                      ? props.index > 0
                      : props.index < props.all.length - 1;

    return (
    <button type="button"
            aria-label={caption}
            title={caption}
            disabled={!enabled}
            className={cx(
              'w-[28px] h-[28px] rounded-[6px] border-solid border-1 bg-white',
              'flex items-center justify-center shrink-0',
              enabled
                ? 'border-[#D8DCE3] cursor-pointer hover:bg-[#F7F9FC]'
                : 'border-[#EEF0F4] cursor-default',
            )}
            onClick={() => enabled && props.move(delta)}>
      <span className={cx(
              'material-icons !text-[18px]',
              enabled ? '!text-[#5A6474]' : '!text-[#C9D1DE]',
            )}>
        { icon }
      </span>
    </button>
    );
  };

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full rounded-[8px] border-solid border-1 border-[#E6E9EF]">

    <FlexRow className="px-[16px] py-[12px] gap-[10px] w-full">
      <span className="text-[15px] font-semibold text-[#1F2733] whitespace-nowrap">
        { t('epg:sources:order', { n: props.index + 1 }) }
      </span>
      <FlexRow flex={1} />
      <FlexRow flex={0} className="gap-[6px]">
        { arrow('arrow_upward', -1, t('epg:sources:move_up')) }
        { arrow('arrow_downward', 1, t('epg:sources:move_down')) }
      </FlexRow>
      <FlexRow flex={0}
               className="gap-[6px] cursor-pointer select-none"
               onClick={() => props.remove()}>
        <span className="material-icons !text-[16px] !text-[#D64545]">delete_outline</span>
        <span className="text-[13px] font-medium text-[#D64545]">
          { t('epg:sources:remove') }
        </span>
      </FlexRow>
    </FlexRow>

    <FlexRow direction="column"
             align="start"
             className="px-[16px] pt-[18px] pb-[16px] gap-[16px] w-full border-t-1 border-solid border-[#EEF0F4]">

      {/* Health for the SAVED version of this row. */}
      <FlexRow direction="column" align="start" className="gap-[6px] w-full">
        <FlexRow className="gap-[8px] flex-wrap">
          <Pill tone={STATUS_TONE[kind]} dot={true} label={t('epg:status:' + kind)} />

          { st && kind !== 'disabled' &&
            <Pill tone="info"
                  label={t('epg:status:counts', {
                    channels:	st.channels || 0,
                    programmes:	st.programmes || 0,
                  })} />
          }

          {/* The ONLY first-wins signal. Without it an admin sees a configured
              feed contributing nothing and reports data loss. */}
          { dropped > 0 &&
            <Pill tone="warn" label={t('epg:status:dropped', { count: dropped })} />
          }

          { pending && src.savedIndex >= 0 &&
            <Pill tone="warn" label={t('epg:status:pending')} />
          }
        </FlexRow>

        { st && st.last_success &&
          <span className={su.hint}>
            { t('epg:status:last_success', { when: formatWhen(st.last_success) }) }
          </span>
        }

        { st && st.error &&
          <span className="text-[12px] leading-[16px] text-[#B14545]">
            { t('epg:status:error_label') } { st.error }
          </span>
        }
      </FlexRow>

      <SettingsInput label={t('epg:sources:url')}
                     required={true}
                     placeholder={t('epg:sources:url_placeholder')}
                     value={src.url}
                     invalid={props.triedSave && err !== 'none'}
                     error={err === 'duplicate'
                              ? t('epg:sources:err_duplicate')
                              : t('epg:sources:err_url')}
                     isLoaded={props.isLoaded}
                     setValue={(v) => props.update({ url: v })} />

      <SettingsInput label={t('epg:sources:name')}
                     placeholder="primetime-core"
                     value={src.name}
                     hint={t('epg:sources:name_hint')}
                     isLoaded={props.isLoaded}
                     setValue={(v) => props.update({ name: v })} />

      <ToggleRow value={!src.disabled}
                 caption={t('epg:sources:enabled')}
                 setValue={(v) => props.update({ disabled: !v })} />
    </FlexRow>
  </FlexRow>
  );
};

// ---------------------------------------------------------------------------

const Epg = () => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);
  const masterMain = useAppSelector((state) => state.master);

  // Select the list DIRECTLY off the store. It is deliberately absent from
  // copyMasterInfo: anything in that helper is a field a page can carry into
  // its POST, and the backend replaces list-shaped fields wholesale when the
  // key is present — so a stale buffer would roll back another admin's edits.
  const savedSources = masterMain.epg_sources;

  const [sources, setSources] = React.useState([] as SourceEdit[]);
  const [triedSave, setTriedSave] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [confirmEmpty, setConfirmEmpty] = React.useState(false);

  const [status, setStatus] = React.useState(null as EpgStatus | null);
  const [statusFailed, setStatusFailed] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  const recheckRef = React.useRef(0 as any);

  const admin = !!role?.admin;

  React.useEffect(() => {
    store.getMaster();
  }, []);

  // Drop local edits whenever the Redux master refreshes (i.e. after a save).
  // No epg_url → row-1 fallback here: `epg_sources` on GET is the RESOLVED
  // list, so an un-migrated deployment already gets its legacy URL back as a
  // single synthesised row.
  React.useEffect(() => {
    setSources(toSourceEdits(savedSources));
    setTriedSave(false);
  }, [savedSources]);

  React.useEffect(() => {
    return () => {
      if (recheckRef.current)
        clearTimeout(recheckRef.current);
    };
  }, []);

  const savedCount = (savedSources || []).length;

  const changes = React.useMemo(
    () => listChanged(sources, savedCount),
    [sources, savedCount]
  );

  const anyInvalid = React.useMemo(() => sourcesInvalid(sources), [sources]);

  // A plain fetch, on purpose. Routed through the global API helper instead, a
  // 403/503 would raise an undismissable red toast on every poll, and a 401 on
  // an idled-out session would force-log-out the admin MID-EDIT, discarding
  // unsaved rows. The endpoint also returns a raw object rather than the
  // {errors:[...]} envelope, so the helper would pop a success toast every 30s.
  const loadStatus = React.useCallback(async () => {
    try {
      const res = await fetch(epgStatusUrl, { method: 'GET' });

      if (!res.ok) {
        setStatusFailed(true);
        return;
      }

      setStatus(copyEpgStatus(await res.json()));
      setStatusFailed(false);
    } catch (e) {
      console.error('Epg/loadStatus/failed', e);
      setStatusFailed(true);
    }
  }, []);

  // See startStatusPoll: the guard runs before the first call, and each tick is
  // gated on tab visibility so a forgotten background tab cannot hold the admin
  // session open forever.
  React.useEffect(
    () => startStatusPoll({
      admin:		admin,
      changes:		changes,
      load:			loadStatus,
      isVisible:	() => document.visibilityState === 'visible',
    }),
    [loadStatus, changes, admin]
  );

  const updateSource = (index: number, patch: Partial<SourceEdit>) => {
    setSources((prev) => prev.map((s, j) => (j === index ? { ...s, ...patch } : s)));
  };

  const performSave = React.useCallback(async () => {
    setSaving(true);
    try {
      const mess = await store.setMaster({ epg_sources: buildEpgSourcesPayload(sources) });

      if (!checkNoError(mess))
        return;

      setTriedSave(false);
      // No second getMaster(): setMaster already refetches on code == 0, and a
      // second call races the buffer resync above.
    } finally {
      setSaving(false);
    }
  }, [sources]);

  const handleSave = React.useCallback(() => {
    if (anyInvalid) {
      setTriedSave(true);
      return;
    }

    // Saving [] is allowed and turns the guide off on every TV — and clears the
    // legacy epg_url fallback with it. Never let that go through on one click.
    if (!sources.length) {
      setConfirmEmpty(true);
      return;
    }

    void performSave();
  }, [anyInvalid, sources.length, performSave]);

  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(epgStatusUrl, { method: 'POST', body: '{}' });

      if (!res.ok) {
        setStatusFailed(true);
        return;
      }

      setStatus(copyEpgStatus(await res.json()));
      setStatusFailed(false);

      // The refetch it forces is asynchronous — the body above still describes
      // the pre-refresh state. Look again once it has had a moment. Only the
      // latest handle is tracked, so drop any earlier one rather than leaving
      // it to fire after a second click.
      if (recheckRef.current)
        clearTimeout(recheckRef.current);

      recheckRef.current = setTimeout(() => loadStatus(), REFRESH_RECHECK_MS);
    } catch (e) {
      console.error('Epg/handleRefresh/failed', e);
      setStatusFailed(true);
    } finally {
      setRefreshing(false);
    }
  }, [loadStatus]);

  const header = (
    <FlexRow direction="column" align="start" className="pb-[4px]">
      <span className={su.pageTitle}>{ t('epg:caption') }</span>
    </FlexRow>
  );

  // The /settings/epg route itself is not admin-gated — only the sidebar link
  // is — so the page has to gate itself.
  if (!admin) {
    return (
      <div className={su.page}>
        <div className={su.pageInner}>
          { header }
          <SettingsCard icon="lock"
                        title={t('epg:caption')}
                        subtitle={t('epg:admin_only')} />
        </div>
      </div>
    );
  }

  const isLoaded = !masterMain.loading;
  const canAdd = canAddSource(sources);

  return (
  <React.Fragment>
    <div className={su.page}>
      <div className={su.pageInner}>
        { header }

        <SettingsCard icon="playlist_play"
                      title={t('epg:sources:caption')}
                      subtitle={t('epg:sources:hint_order')}>

          {/* Keyed on the synthetic id, never on the array index or the URL. */}
          { sources.map((src, i) =>
            <SourceRow key={src.id}
                       source={src}
                       index={i}
                       all={sources}
                       triedSave={triedSave}
                       isLoaded={isLoaded}
                       status={status}
                       statusFailed={statusFailed}
                       update={(patch) => updateSource(i, patch)}
                       move={(delta) => setSources((prev) => moveSource(prev, i, delta))}
                       remove={() => setSources((prev) => removeSource(prev, i))} />
          )}

          { !sources.length &&
            <InfoBanner tone="warn" text={t('epg:sources:empty')} />
          }

          { canAdd
              ? <AddRow caption={t('epg:sources:add')}
                        onClick={() => setSources((prev) => addSource(prev))} />
              : <InfoBanner text={t('epg:sources:at_max', { max: EPG_MAX_SOURCES })} />
          }

          <InfoBanner text={t('epg:sources:hint_same')} />
          <InfoBanner text={t('epg:sources:hint_broken')} />
        </SettingsCard>

        <SettingsCard icon="favorite_border"
                      title={t('epg:status:caption')}
                      status={
                        <Button caption={refreshing
                                           ? t('epg:status:refreshing')
                                           : t('epg:status:refresh')}
                                icon="sync"
                                layout="white"
                                isDisabled={refreshing}
                                onClick={() => handleRefresh()} />
                      }>

          { status &&
            <FlexRow direction="column" align="start" className="gap-[6px] w-full">
              <span className="text-[14px] font-medium text-[#1F2733]">
                { t('epg:status:merged', {
                    channels:	status.channels || 0,
                    programmes:	status.programmes || 0,
                  }) }
              </span>
              <span className={su.hint}>
                { status.last_update
                    ? t('epg:status:updated', { when: formatWhen(status.last_update) })
                    : t('epg:status:unknown') }
              </span>
            </FlexRow>
          }

          {/* One inline note, not a toast storm. */}
          { statusFailed &&
            <InfoBanner tone="warn" text={t('epg:status:unavailable')} />
          }
        </SettingsCard>
      </div>
    </div>

    <FlexRow className="sticky bottom-0 z-20 bg-white border-t-1 border-solid border-[#E6E9EF] px-[32px] py-[14px]">
      <FlexRow flex={1} className="gap-[8px] items-center">
        { changes &&
          <React.Fragment>
            <span className="material-icons !text-[18px] !text-[#B98B36]">edit</span>
            <span className="text-[13px] text-[#8A6A2F]">
              { t('epg:sources:unsaved_hint') }
            </span>
          </React.Fragment>
        }
      </FlexRow>
      <FlexRow flex={0}>
        <Button caption={t('epg:save:caption')}
                icon="save"
                isDisabled={!changes || saving || (triedSave && anyInvalid)}
                onClick={() => handleSave()} />
      </FlexRow>
    </FlexRow>

    { confirmEmpty &&
      <ButtonsModal title={t('epg:sources:empty')}
                    message={t('epg:sources:empty_confirm')}
                    onResult={(_obj, result) => {
                      setConfirmEmpty(false);

                      if (result === 'yes')
                        void performSave();
                    }}>
        <></>
      </ButtonsModal>
    }
  </React.Fragment>
  );
};

export default Epg;
